import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const plum = '#1E1040'

// Card types — labels are translated via t(). Icons live alongside for display.
const CARD_TYPES = [
  { value: 'credit', icon: '💳', requiresLast4: true },
  { value: 'debit', icon: '💳', requiresLast4: true },
  { value: 'cash', icon: '💵', requiresLast4: false },
  { value: 'check', icon: '📝', requiresLast4: false },
  { value: 'other', icon: '🔗', requiresLast4: false },
]

const cardTypeMeta = (type) => CARD_TYPES.find(c => c.value === type) || CARD_TYPES[0]

/**
 * AddPaymentMethodInline
 * Small inline form for adding a payment method without leaving the receipt modal.
 * Auto-selects the new payment method after save.
 *
 * Props:
 *   defaultTagId: uuid — pre-fills the default tag (usually matches current receipt's tag)
 *   onAdded: (newPaymentMethod) => void
 *   onCancel: () => void
 */
export default function AddPaymentMethodInline({ defaultTagId = null, onAdded, onCancel }) {
  const { t } = useTranslation()
  const [label, setLabel] = useState('')
  const [cardType, setCardType] = useState('credit')
  const [last4, setLast4] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const meta = cardTypeMeta(cardType)

  const handleSave = async () => {
    const trimmed = label.trim()
    if (!trimmed) { setError(t('add_payment.error_label_required')); return }
    if (trimmed.length > 40) { setError(t('add_payment.error_label_too_long')); return }

    if (meta.requiresLast4) {
      if (!last4) { setError(t('add_payment.error_last4_required')); return }
      if (!/^\d{4}$/.test(last4)) { setError(t('add_payment.error_last4_format')); return }
    }

    setSaving(true)
    setError('')

    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error(t('add_payment.error_not_signed_in'))

      // Count existing payment methods to determine is_default + sort_order
      const { data: existing } = await supabase
        .from('payment_methods')
        .select('id')
        .eq('user_id', user.id)

      const count = existing?.length || 0

      const { data: newPm, error: insertErr } = await supabase
        .from('payment_methods')
        .insert({
          user_id: user.id,
          label: trimmed,
          card_type: cardType,
          last_4: meta.requiresLast4 ? last4 : null,
          default_tag_id: defaultTagId || null,
          is_default: count === 0, // first one added becomes the default
          sort_order: count + 1,
        })
        .select()
        .single()

      if (insertErr) throw insertErr

      onAdded?.(newPm)
    } catch (err) {
      setError(err.message || t('add_payment.error_generic'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={styles.box}>
      <div style={styles.headerRow}>
        <div style={styles.label}>{t('add_payment.title')}</div>
        <button type="button" style={styles.closeBtn} onClick={onCancel} aria-label={t('common.cancel')}>✕</button>
      </div>

      <div style={styles.field}>
        <label style={styles.fieldLabel}>{t('add_payment.label_field')}</label>
        <input
          type="text"
          style={styles.input}
          placeholder={t('add_payment.label_placeholder')}
          value={label}
          onChange={e => setLabel(e.target.value)}
          maxLength={40}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); handleSave() }
            if (e.key === 'Escape') { e.preventDefault(); onCancel?.() }
          }}
        />
      </div>

      <div style={styles.fieldRow}>
        <div style={styles.field}>
          <label style={styles.fieldLabel}>{t('add_payment.type_field')}</label>
          <select
            style={styles.input}
            value={cardType}
            onChange={e => {
              setCardType(e.target.value)
              const newMeta = cardTypeMeta(e.target.value)
              if (!newMeta.requiresLast4) setLast4('')
            }}
          >
            {CARD_TYPES.map(ct => (
              <option key={ct.value} value={ct.value}>{ct.icon} {t(`add_payment.types.${ct.value}`)}</option>
            ))}
          </select>
        </div>

        {meta.requiresLast4 && (
          <div style={styles.field}>
            <label style={styles.fieldLabel}>{t('add_payment.last4_field')}</label>
            <input
              type="text"
              inputMode="numeric"
              style={styles.input}
              placeholder="1234"
              value={last4}
              onChange={e => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
              maxLength={4}
            />
          </div>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.actions}>
        <button type="button" style={styles.btnCancel} onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          style={{ ...styles.btnSave, opacity: (!label.trim() || saving) ? 0.5 : 1 }}
          onClick={handleSave}
          disabled={!label.trim() || saving}
        >
          {saving ? t('add_payment.adding') : t('add_payment.add_cta')}
        </button>
      </div>
      <div style={styles.hint}>
        {t('add_payment.hint')}
      </div>
    </div>
  )
}

const styles = {
  box: {
    background: '#F9FAFB',
    border: `2px solid ${teal}`,
    borderRadius: '12px',
    padding: '14px',
    marginTop: '8px',
    marginBottom: '10px',
  },
  headerRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '12px',
  },
  label: {
    fontSize: '11px', fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  closeBtn: {
    background: 'transparent', border: 'none',
    fontSize: '16px', color: '#9CA3AF', cursor: 'pointer', padding: '2px 6px',
  },
  field: { marginBottom: '10px' },
  fieldRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  fieldLabel: {
    display: 'block', fontSize: '11px', fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '5px',
  },
  input: {
    width: '100%', padding: '9px 12px', borderRadius: '8px',
    border: '1.5px solid #E5E7EB', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#fff',
  },
  error: {
    background: '#FEE2E2', color: '#991B1B',
    padding: '8px 12px', borderRadius: '8px',
    fontSize: '12px', fontWeight: 500, marginTop: '8px',
  },
  actions: { display: 'flex', gap: '8px', marginTop: '12px' },
  btnCancel: {
    flex: '0 0 auto', padding: '9px 16px',
    background: '#fff', color: '#6B7280',
    border: '1.5px solid #E5E7EB', borderRadius: '100px',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnSave: {
    flex: 1, padding: '9px 16px',
    background: teal, color: '#042f2e',
    border: 'none', borderRadius: '100px',
    fontSize: '12px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  hint: {
    fontSize: '11px', color: '#9CA3AF',
    marginTop: '8px', fontWeight: 500, lineHeight: 1.4, textAlign: 'center',
  },
}
